import { useState } from 'react';
import { EmailTextSelect, Pill, RelativeTime, cn, formatDkk, invoiceStatusPill } from '@jywer/ui';
import { PostponeModal } from './PostponeModal';
import { InvoiceRowExpansion } from './InvoiceRowExpansion';
import {
  approveAndSend,
  approveGroup,
  approveInvoice,
  csvExportUrl,
  editInvoice,
  markSent,
  postponeInvoice,
} from './api';
import type { BillingClientGroup } from './types';

export function ClientGroup({
  group,
  onChanged,
}: {
  group: BillingClientGroup;
  onChanged: () => Promise<void>;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [postponeId, setPostponeId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pendingCount = group.invoices.filter(
    (i) => i.status === 'draft' || i.status === 'postponed',
  ).length;
  const approvedCount = group.invoices.filter((i) => i.status === 'approved').length;
  const groupTotal = group.invoices.reduce((sum, i) => sum + Number(i.amount || 0), 0);

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runAction = async (fn: () => Promise<unknown>, id: string) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukendt fejl');
    } finally {
      setBusy(null);
    }
  };

  const approveAll = async () => {
    if (pendingCount === 0) return;
    if (!confirm(`Godkend ${pendingCount} fakturaer for ${group.clientName}?`)) return;
    await runAction(() => approveGroup(group.clientId), `group-${group.clientId}`);
  };

  const approvedIds = group.invoices
    .filter((i) => i.status === 'approved')
    .map((i) => i.invoice_id);

  return (
    <section className="mb-4 rounded border border-line-1 bg-paper-0">
      <header className="flex items-center justify-between border-b border-line-1 bg-paper-2/30 px-4 py-2">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 text-left"
        >
          <span className="text-ink-2">{collapsed ? '▶' : '▼'}</span>
          <span className="font-semibold text-primary">{group.clientName}</span>
          <span className="text-sm text-ink-2">
            {group.invoices.length} fakturaer · {formatDkk(groupTotal)}
          </span>
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={approveAll}
            disabled={pendingCount === 0 || busy !== null}
            className={cn(
              'rounded bg-status-err px-3 py-1.5 text-xs font-medium text-white hover:bg-status-err/90',
              (pendingCount === 0 || busy !== null) && 'opacity-50',
            )}
          >
            Godkend alle ({pendingCount})
          </button>
          {approvedCount > 0 ? (
            <a
              href={csvExportUrl(approvedIds)}
              className="rounded border border-accent px-3 py-1.5 text-xs text-accent hover:bg-accent/5"
              download
            >
              Eksporter CSV ({approvedCount})
            </a>
          ) : null}
        </div>
      </header>

      {error ? (
        <div className="border-b border-status-err/40 bg-status-err/5 px-4 py-2 text-sm text-status-err">
          {error}
        </div>
      ) : null}

      {!collapsed ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-1 text-left text-xs text-ink-2">
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Beløb</th>
              <th className="px-3 py-2">Beskrivelse</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Email-tekst</th>
              <th className="px-3 py-2">Udløst</th>
              <th className="px-3 py-2 text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {group.invoices.map((inv) => {
              const pill = invoiceStatusPill(inv.status);
              const expanded = expandedIds.has(inv.invoice_id);
              return (
                <>
                  <tr
                    key={inv.invoice_id}
                    className={cn(
                      'cursor-pointer border-b border-line-1 hover:bg-paper-2/30',
                      expanded && 'bg-paper-2/40',
                    )}
                    onClick={() => toggle(inv.invoice_id)}
                  >
                    <td className="px-3 py-2">{invoiceTypeLabel(inv.invoice_type)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatDkk(inv.amount)}
                    </td>
                    <td className="px-3 py-2 text-ink-1" title={inv.description ?? ''}>
                      <span className="line-clamp-1">{inv.description ?? '—'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Pill variant={pill.variant}>{pill.label}</Pill>
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {inv.status === 'approved' ? (
                        <span className="text-xs text-ink-3" title={inv.email_custom_text ?? ''}>
                          {inv.email_custom_text ? (
                            <span className="line-clamp-1 max-w-[18ch]">
                              {inv.email_custom_text}
                            </span>
                          ) : (
                            '—'
                          )}
                        </span>
                      ) : (
                        <EmailTextSelect
                          value={inv.email_custom_text}
                          onChange={(next) =>
                            runAction(
                              () => editInvoice(inv.invoice_id, { email_custom_text: next }),
                              inv.invoice_id,
                            )
                          }
                          disabled={busy === inv.invoice_id}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-2">
                      <RelativeTime value={inv.triggered_at} showDot={false} />
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {inv.status === 'approved' ? (
                        <button
                          type="button"
                          onClick={() => runAction(() => markSent(inv.invoice_id), inv.invoice_id)}
                          disabled={busy === inv.invoice_id}
                          className="rounded border border-accent px-2 py-1 text-xs text-accent hover:bg-accent/5"
                        >
                          {busy === inv.invoice_id ? 'Markerer…' : 'Marker som sendt'}
                        </button>
                      ) : (
                        <span className="text-xs text-ink-3">klik for at redigere</span>
                      )}
                    </td>
                  </tr>
                  {expanded ? (
                    <tr
                      key={`${inv.invoice_id}-exp`}
                      className="border-b border-line-1 bg-paper-2/20"
                    >
                      <td colSpan={7} className="px-4 py-4">
                        <InvoiceRowExpansion
                          invoice={inv}
                          onApprove={() =>
                            runAction(() => approveInvoice(inv.invoice_id), inv.invoice_id)
                          }
                          onApproveAndSend={() =>
                            runAction(() => approveAndSend(inv.invoice_id), inv.invoice_id)
                          }
                          onPostpone={() => setPostponeId(inv.invoice_id)}
                          onChanged={onChanged}
                        />
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {postponeId ? (
        <PostponeModal
          onCancel={() => setPostponeId(null)}
          onSubmit={async (body) => {
            await postponeInvoice(postponeId, body);
            await onChanged();
            setPostponeId(null);
          }}
        />
      ) : null}
    </section>
  );
}

function invoiceTypeLabel(t: string): string {
  switch (t) {
    case 'project_rate':
      return 'Projekt-rate';
    case 'subscription_setup':
      return 'Abonnement-setup';
    case 'ad_hoc':
      return 'Ad hoc';
    case 'pakkelosning_combined':
      return 'Pakkeløsning';
    default:
      return t;
  }
}
